import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserIntegrators, loggedInSelector } from 'redux/modules/auth';
import {
  clearDownload,
  loadDownloadData,
} from 'redux/modules/download/actions';
import { GetVideoExportStatusError } from 'redux/modules/download/types';
import { showError } from 'redux/modules/notification/actions';
import { goToAppHome } from 'redux/modules/router/actions';

function logDownloadPageError(
  wid: string,
  isLoggedIn: boolean,
  error: any,
): void {
  Sentry.withScope(scope => {
    scope.setTag('messageFilter', 'Error loading video data');
    scope.setTag('wid', wid);
    scope.setTag('isLoggedIn', isLoggedIn.toString());
    scope.setExtra('errorDetails', error);
    scope.setExtra('requestContext', {
      wid,
      isLoggedIn,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
    scope.setLevel(Severity.Error);
    Sentry.captureException(error);
  });
}

export default function useDownloadPageDispatch(wid: string) {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(loggedInSelector);

  useEffect(
    () => () => {
      dispatch(clearDownload());
    },
    [dispatch],
  );

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        if (isLoggedIn) {
          return dispatch(getUserIntegrators());
        }
        return null;
      })
      .then(() => dispatch(loadDownloadData(wid)))
      .catch(err => {
        logDownloadPageError(wid, isLoggedIn, err);

        if (err instanceof GetVideoExportStatusError) {
          dispatch(
            showError({
              message: 'Invalid Download URL',
              code: 'IN017',
              dismissAfterSec: 5,
            }),
          );
        } else {
          dispatch(
            showError({
              message: 'Error loading video data',
              code: 'ER014',
              dismissAfterSec: 5,
            }),
          );
        }

        dispatch(goToAppHome());
      });
  }, [dispatch, isLoggedIn, wid]);
}
